import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import Chip from '@Shared.Angular/flowingly.components/Chip/Chip';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';
import KendoReactColorGradient from '@Shared.Angular/kendo.react/KendoReactColorGradient';

type Props = {
  onClose: () => void;
  onActionClick: (colors: string[]) => void;
  selectedColors: string[];
};

const MAX_COLOR_LENGTH = 7;
const DEFAULT_SYSTEM_COLOR = '#35384D';
const MAX_FONT_COLORS = 60;

const FontColorPickerModal: React.FC<Props> = ({
  selectedColors,
  onClose,
  onActionClick
}) => {
  const [colors, setColors] = useState<string[]>([]);
  const [colorInput, setColorInput] = useState('');
  const [colorError, setColorError] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Always ensure default color is first
    const colorsToSet = selectedColors?.length > 0 ? [...selectedColors] : [];

    // Remove default color if it exists elsewhere
    const filtered = colorsToSet.filter(
      (c) => c.toUpperCase() !== DEFAULT_SYSTEM_COLOR.toUpperCase()
    );

    // Add default color at the start
    setColors([DEFAULT_SYSTEM_COLOR, ...filtered]);
  }, []);

  useEffect(() => {
    if (!showColorPicker) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;

      // Don't close if clicking inside the color picker container
      if (colorPickerRef.current && colorPickerRef.current.contains(target)) {
        return;
      }

      // Don't close if clicking on the text input
      if (colorInputRef.current && colorInputRef.current.contains(target)) {
        return;
      }

      // Don't close if clicking on a button (Cancel/Save/Choose Color/Remove All buttons)
      if (target.closest('button')) {
        return;
      }

      setShowColorPicker(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showColorPicker]);

  const handleColorInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setColorInput(event.target.value);
    setColorError('');
  };

  // Normalize 3-digit hex to 6-digit for comparison
  const normalizeHexColor = (hex: string): string => {
    const normalized = hex.toUpperCase();
    if (normalized.length === 4) {
      return `#${normalized[1]}${normalized[1]}${normalized[2]}${normalized[2]}${normalized[3]}${normalized[3]}`;
    }
    return normalized;
  };

  const isColorCodeValid = (
    colorCode: string,
    originalCode?: string
  ): boolean => {
    const hexPattern = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/;

    if (!hexPattern.test(colorCode)) {
      const message =
        'Invalid colour code. Please enter a valid hex value (e.g., #FFF or #FFFFFF).';
      setColorError(message);
      return false;
    }

    const normalizedNewColor = normalizeHexColor(colorCode);
    const normalizedOriginal = originalCode
      ? normalizeHexColor(originalCode)
      : '';

    if (
      colors.some((color) => {
        const normalizedExisting = normalizeHexColor(color);
        return (
          normalizedExisting === normalizedNewColor &&
          normalizedExisting !== normalizedOriginal
        );
      })
    ) {
      const message = `The colour '${colorCode}' already exists.`;
      setColorError(message);
      return false;
    }

    return true;
  };

  const handleColorKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || !colorInput.trim()) return;

    event.preventDefault();
    let newColorCode = colorInput.trim();

    // Automatically add # prefix if missing
    if (!newColorCode.startsWith('#')) {
      newColorCode = `#${newColorCode}`;
    }

    // Uppercase for consistency
    newColorCode = newColorCode.toUpperCase();

    // Normalize 3-digit hex to 6-digit for uniform storage
    newColorCode = normalizeHexColor(newColorCode);

    if (colors.length >= MAX_FONT_COLORS) {
      const message = `You can only add up to ${MAX_FONT_COLORS} colors.`;
      setColorError(message);
      return;
    }
    if (isColorCodeValid(newColorCode)) {
      setColors([...colors, newColorCode]);
      setColorInput('');
      setColorError('');
    }
  };

  const clearValidationError = () => {
    setColorError('');
  };

  const onRemoveColor = (color: string) => {
    const fullColorWithHash = `#${color}`;

    if (fullColorWithHash === DEFAULT_SYSTEM_COLOR) {
      return;
    }

    const newColors = colors.filter((c) => c !== fullColorWithHash);
    setColors(newColors);
  };

  const onRemoveAllColors = () => {
    setColors([DEFAULT_SYSTEM_COLOR]);
  };

  const saveFontColors = () => {
    onActionClick(colors);
    onClose();
  };

  const toggleColorPicker = () => {
    setShowColorPicker(!showColorPicker);
  };

  const handlePickerColorChange = (hexColor: string) => {
    setColorInput(hexColor);
    setColorError('');
    colorInputRef.current?.focus();
  };

  return (
    <Modal
      title="Custom Font Colour"
      onClose={onClose}
      onCancelClick={onClose}
      onActionClick={saveFontColors}
      size="md"
      actionButtonText="Save"
    >
      <div className="font-color-selector-outer-container">
        <div className="font-color-selector-container">
          {colors.map((color) => {
            const isDefaultColor =
              color.toUpperCase() === DEFAULT_SYSTEM_COLOR.toUpperCase();

            return (
              <Chip
                key={color}
                name={color.replace('#', '')}
                color={'EBEBEB'}
                fontColor="#000000"
                onRemoveChip={onRemoveColor}
                canRemove={!isDefaultColor}
                canEdit={false}
                clearValidationError={clearValidationError}
                showSwatch={true}
                swatchColor={color}
                title={
                  isDefaultColor
                    ? 'Default System Font Colour cannot be removed'
                    : undefined
                }
              />
            );
          })}
          <div className="font-color-input-wrapper">
            <i className="fa-plus"></i>
            <input
              ref={colorInputRef}
              type="text"
              value={colorInput}
              onChange={handleColorInputChange}
              onKeyDown={handleColorKeyDown}
              placeholder="Enter colour hex code (e.g. 000000)"
              maxLength={MAX_COLOR_LENGTH}
            />
          </div>
        </div>

        <div className="font-color-actions">
          <TextButton
            id="font-color-choose-colour-button"
            text="Choose Colour"
            type="text-only"
            classNames="btn-text-only choose-color-btn"
            onClick={toggleColorPicker}
          />
          {colors.length > 1 && (
            <TextButton
              id="font-color-remove-all-button"
              text="Remove All"
              type="text-only"
              classNames="btn-text-only remove-all-color-btn"
              onClick={onRemoveAllColors}
            />
          )}
        </div>

        {showColorPicker && (
          <div className="font-color-picker-color-wrapper" ref={colorPickerRef}>
            <KendoReactColorGradient
              format="hex"
              opacity={false}
              value={colorInput}
              onChange={handlePickerColorChange}
            />
          </div>
        )}
        {colorError && (
          <div className="validation-errors">
            <p className="error-message-on-submit">{colorError}</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default FontColorPickerModal;
